import { useLanguage } from '../context/LanguageContext';
import { X } from 'lucide-react';

const TermsModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();

  if (!isOpen) return null;

  const content = language === 'he' ? {
    title: 'תנאי שימוש',
    sections: [
      {
        heading: '1. כללי',
        text: 'ברוכים הבאים לאתר של Eran MH Coaching (להלן "האתר"). השימוש באתר מהווה הסכמה לתנאי השימוש המפורטים להלן. אם אינך מסכים לתנאים אלו, אנא הימנע משימוש באתר.',
      },
      {
        heading: '2. שירותים',
        text: 'האתר מספק מידע אודות שירותי אימון אישי בשיטת סאטיה. התכנים באתר הם למטרות מידע בלבד ואינם מהווים תחליף לייעוץ מקצועי, טיפול רפואי או פסיכולוגי.',
      },
      {
        heading: '3. פרטיות ומידע אישי',
        text: 'מידע אישי שנמסר באמצעות טופס יצירת הקשר (שם, טלפון, מדינה, זמן מועדף) ישמש אך ורק לצורך יצירת קשר חוזר ולא יועבר לצד שלישי. המידע נשמר באופן מאובטח.',
      },
      {
        heading: '4. עוגיות (Cookies)',
        text: 'האתר משתמש בעוגיות לשיפור חוויית הגלישה ולצרכי אנליטיקה. באפשרותך לנהל את העדפות העוגיות דרך באנר העוגיות.',
      },
      {
        heading: '5. קניין רוחני',
        text: 'כל התכנים באתר, לרבות טקסטים, תמונות, עיצוב גרפי ולוגו, הם קניינו הרוחני של Eran MH Coaching ומוגנים בזכויות יוצרים. אין להעתיק, לשכפל או להפיץ תכנים מהאתר ללא אישור מראש.',
      },
      {
        heading: '6. הגבלת אחריות',
        text: 'האימון בשיטת סאטיה הוא תהליך אישי שתוצאותיו משתנות מאדם לאדם. אין להתייחס לתכנים באתר או לתוצאות האימון כהבטחה או אחריות לתוצאה ספציפית.',
      },
      {
        heading: '7. שינויים בתנאים',
        text: 'Eran MH Coaching שומר על הזכות לעדכן תנאי שימוש אלו מעת לעת. המשך השימוש באתר לאחר שינוי התנאים מהווה הסכמה לתנאים המעודכנים.',
      },
      {
        heading: '8. יצירת קשר',
        text: 'לשאלות בנוגע לתנאי שימוש אלו, ניתן לפנות בדוא"ל: eran.mazor86@gmail.com',
      },
    ],
    updated: 'תנאי שימוש אלו עודכנו לאחרונה: אפריל 2026',
  } : {
    title: 'Terms of Use',
    sections: [
      {
        heading: '1. General',
        text: 'Welcome to the Eran MH Coaching website ("the Website"). Using this website constitutes agreement to the following terms of use. If you do not agree to these terms, please refrain from using the website.',
      },
      {
        heading: '2. Services',
        text: 'This website provides information about personal coaching services using the Satya method. The content is for informational purposes only and does not constitute a substitute for professional advice, medical treatment, or psychological therapy.',
      },
      {
        heading: '3. Privacy & Personal Information',
        text: 'Personal information submitted through the contact form (name, phone, country, preferred time) will be used solely for the purpose of follow-up contact and will not be shared with third parties. Information is stored securely.',
      },
      {
        heading: '4. Cookies',
        text: 'This website uses cookies to improve browsing experience and for analytics purposes. You can manage your cookie preferences through the cookie banner.',
      },
      {
        heading: '5. Intellectual Property',
        text: 'All content on this website, including text, images, graphic design, and logo, is the intellectual property of Eran MH Coaching and is protected by copyright. No content may be copied, reproduced, or distributed without prior permission.',
      },
      {
        heading: '6. Limitation of Liability',
        text: 'Coaching using the Satya method is a personal process whose results vary from person to person. Website content and coaching outcomes should not be regarded as a promise or guarantee of specific results.',
      },
      {
        heading: '7. Changes to Terms',
        text: 'Eran MH Coaching reserves the right to update these terms from time to time. Continued use of the website after changes constitutes acceptance of the updated terms.',
      },
      {
        heading: '8. Contact',
        text: 'For questions regarding these terms of use, please contact: eran.mazor86@gmail.com',
      },
    ],
    updated: 'These terms were last updated: April 2026',
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
      data-testid="terms-modal-overlay"
    >
      <div 
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8 md:p-10 relative"
        onClick={(e) => e.stopPropagation()}
        dir={language === 'he' ? 'rtl' : 'ltr'}
        data-testid="terms-modal"
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          data-testid="terms-close"
        >
          <X size={20} />
        </button>

        <h2 
          className="text-2xl md:text-3xl mb-6"
          style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--charcoal)' }}
        >
          {content.title}
        </h2>

        <div className="space-y-6">
          {content.sections.map((section, i) => (
            <div key={i}>
              <h3 className="font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                {section.heading}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm">{section.text}</p>
            </div>
          ))}
        </div>

        <p className="text-gray-400 text-xs mt-8">{content.updated}</p>
      </div>
    </div>
  );
};

export default TermsModal;
